import { useMemo } from "react";
import { Box, Button, Theme, Tooltip, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  IVacationAwardingCrew,
  IVacationAwardingSlotUsage,
  IVacationBidSlot,
  VacationAwardingProgressStatus,
  VacationBidRound,
} from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";
import { displaySlotId } from "../helpers";
import {
  awardDateRange,
  awardSourceLabel,
  awardedDays,
  crewName,
  entitlementUse,
  openWarnings,
  shortBlockAction,
  shortBlockLabel,
} from "./awardingHelpers";
import { AwardWarnings } from "./AwardWarnings";
import { CrewLeftoverDays, LeftoverPlacement } from "./CrewLeftoverDays";
import { RankedBidList, SlotAvailability } from "./RankedBidList";

export interface ICrewAwardPanelProps {
  crew: IVacationAwardingCrew;
  round: VacationBidRound;
  slotsById: Map<string, IVacationBidSlot>;
  usageBySlot: Map<string, IVacationAwardingSlotUsage>;
  showSlotIdYear: boolean;
  maxAwardsFirstRound: number | null | undefined;
  /** False when the round is read-only; every action is then disabled. */
  writesAllowed: boolean;
  /** At the round 1 cap, counting awards still being saved. */
  capReached: boolean;
  /** Holds their whole entitlement, counting awards still being saved. */
  entitlementReached: boolean;
  /** Slots whose award is still being saved. */
  pendingAdds: Set<string>;
  /** Slots whose removal is still being saved. */
  pendingRemoves: Set<string>;
  /** A progress change for this crew member is still being saved. */
  progressPending: boolean;
  /** A leftover-day placement for this crew member is still being saved. */
  leftoverPending: boolean;
  /** A move of this crew member's short block is still being saved. */
  shortBlockPending: boolean;
  onAward: (slotId: string) => void;
  onUndo: (slotId: string) => void;
  onProgress: (status: VacationAwardingProgressStatus | null) => void;
  onPlaceLeftover: (placement: LeftoverPlacement) => void;
  onMakeShort: (slotId: string) => void;
  /** Resolves true once the warning is gone. */
  onDismissWarning: (warningId: string) => Promise<boolean>;
}

/** Ranked bids shown before "Show all". */
const INITIAL_BIDS_SHOWN = 5;

const CrewAwardPanelStyles = (theme: Theme): any => ({
  minHeight: 0,
  overflowY: "auto",
  padding: "12px 14px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.custom.forms.inputBorder}`,
  borderRadius: "3px",
  "& .panel_header": {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    "& .panel_identity": { flex: 1 },
    "& .panel_name": { fontSize: 16, fontWeight: 700 },
    "& .panel_seniority": { fontSize: 12, color: theme.custom.forms.labels, marginLeft: "6px" },
    "& .MuiButtonBase-root": { padding: "2px 10px", fontSize: 12 },
  },
  "& .panel_entitlement": {
    fontSize: 13,
    color: theme.custom.forms.labels,
    "& .awarded_count": { color: theme.palette.success.main },
    "& .leftover_note": { color: theme.palette.warning.light },
  },
  "& .panel_warning": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "6px",
    fontSize: 12,
    color: theme.palette.warning.light,
    "&.unchecked": { color: theme.custom.forms.labels },
  },
  "& .panel_label": {
    fontSize: 11,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
    margin: "12px 0 4px",
    "& .panel_label_hint": { textTransform: "none", marginLeft: "6px" },
  },
  "& .panel_note": {
    padding: "6px 10px",
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.custom.timeline.contextListBackground,
    fontSize: 13,
  },
  "& .panel_empty": { color: theme.custom.forms.labels, fontSize: 13 },
  "& .awarded_label": { fontSize: 12, color: theme.palette.success.main },
  "& .short_label": {
    fontSize: 11,
    padding: "0 6px",
    color: theme.palette.warning.light,
    border: `1px solid ${theme.palette.warning.light}`,
    borderRadius: "3px",
  },
  "& .assigned_row": {
    display: "grid",
    gridTemplateColumns: "104px 120px minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: "12px",
    minHeight: "32px",
    padding: "2px 10px",
    "& .slot_id": { fontWeight: 700, whiteSpace: "nowrap" },
    "& .other_source": { color: theme.custom.forms.labels },
    "& .assigned_actions": { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px" },
    "& .MuiButtonBase-root": { padding: "1px 10px", fontSize: 12, minWidth: 0 },
  },
});

/** One crew member's award: their entitlement, what they ranked, and what they hold. */
export const CrewAwardPanel = (props: ICrewAwardPanelProps): JSX.Element => {
  const {
    crew,
    round,
    slotsById,
    usageBySlot,
    showSlotIdYear,
    maxAwardsFirstRound,
    writesAllowed,
    capReached,
    entitlementReached,
    pendingAdds,
    pendingRemoves,
    progressPending,
    leftoverPending,
    shortBlockPending,
    onAward,
    onUndo,
    onProgress,
    onPlaceLeftover,
    onMakeShort,
    onDismissWarning,
  } = props;

  const use = entitlementUse(crew);
  const progressDisabled = !writesAllowed || progressPending;

  const awardsBySlot = useMemo(() => new Map(crew.awards.map((z) => [z.slotId, z])), [crew.awards]);
  const rankedSlotIds = useMemo(() => new Set((crew.bid?.preferences ?? []).map((z) => z.slotId)), [crew.bid]);
  const shortBlockLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const award of crew.awards) {
      const slot = slotsById.get(award.slotId);
      const label = slot && shortBlockLabel(award, slot);
      if (label) labels.set(award.slotId, label);
    }
    return labels;
  }, [crew.awards, slotsById]);
  // Awards not on this round's ranked bids: assigned by a scheduler, or taken from another round's bid.
  const otherAwards = useMemo(
    () => crew.awards.filter((z) => !rankedSlotIds.has(z.slotId)),
    [crew.awards, rankedSlotIds]
  );

  const fullEntitlement = `Holds their full entitlement of ${
    use.blocks === null ? `${use.totalDays} days` : `${use.blocks} blocks`
  }. Raise it in 2) Add Crew to award more.`;
  const awardBlockedReason = capReached
    ? `Already holds the maximum of ${maxAwardsFirstRound} round 1 awards`
    : entitlementReached
      ? fullEntitlement
      : !writesAllowed
        ? "This round can no longer be changed"
        : "";

  const undoButton = (slotId: string, label: string): JSX.Element => (
    <Button
      size="small"
      variant="outlined"
      disabled={!writesAllowed || pendingRemoves.has(slotId)}
      onClick={() => onUndo(slotId)}
      data-testid={VACATION_BID_TEST_ID.undoAwardButton}
    >
      {label}
    </Button>
  );

  // The short block's marker, or the button that makes this award the short block.
  const shortBlockControl = (slotId: string): JSX.Element | null => {
    const action = shortBlockAction(crew, slotId, slotsById, { writesAllowed, saving: shortBlockPending });
    if (!action) return null;
    if (action.kind === "short") {
      return (
        <span className="short_label" title={action.label} data-testid={VACATION_BID_TEST_ID.shortBlockChip}>
          Short block
        </span>
      );
    }
    return (
      <Tooltip title={action.disabledReason} placement="top">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!!action.disabledReason}
            onClick={() => onMakeShort(slotId)}
            data-testid={VACATION_BID_TEST_ID.makeShortButton}
          >
            Make short
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box sx={CrewAwardPanelStyles} data-testid={VACATION_BID_TEST_ID.crewAwardPanel}>
      <div className="panel_header">
        <div className="panel_identity">
          <Typography component="span" className="panel_name">
            {crewName(crew)}
          </Typography>
          <Typography component="span" className="panel_seniority">
            {crew.seniority === null ? "· No seniority" : `· Seniority ${crew.seniority}`}
          </Typography>
          <Typography className="panel_entitlement">
            {`${use.totalDays} days total`}
            {use.blocks !== null && (
              <>
                {` · ${use.blocks} blocks (`}
                <span className="awarded_count">{`${use.awarded} awarded`}</span>
                {`, ${use.remaining} remaining)`}
              </>
            )}
            {use.blocks === null && ` · ${use.awarded} blocks awarded`}
            {!!use.leftoverDays && (
              <span className="leftover_note">
                {` · ${use.leftoverDays} leftover day${use.leftoverDays === 1 ? "" : "s"}`}
              </span>
            )}
          </Typography>
        </div>
        {crew.progress === VacationAwardingProgressStatus.Complete ? (
          <Button
            size="small"
            variant="outlined"
            disabled={progressDisabled}
            onClick={() => onProgress(null)}
            data-testid={VACATION_BID_TEST_ID.reopenCrewButton}
          >
            Reopen
          </Button>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={progressDisabled}
              onClick={() => onProgress(VacationAwardingProgressStatus.Skipped)}
              data-testid={VACATION_BID_TEST_ID.skipCrewButton}
            >
              Skip for now
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={progressDisabled}
              onClick={() => onProgress(VacationAwardingProgressStatus.Complete)}
              data-testid={VACATION_BID_TEST_ID.completeCrewButton}
            >
              Mark complete
            </Button>
          </>
        )}
      </div>

      {use.over !== null && use.over > 0 && (
        <Typography className="panel_warning">
          <WarningIcon fontSize="inherit" />
          {`Awarded ${use.over} block${use.over === 1 ? "" : "s"} more than their entitlement of ${use.blocks}.`}
        </Typography>
      )}
      {use.over === null && (
        <Typography className="panel_warning unchecked">
          <InfoIcon fontSize="inherit" />
          {`This package's blocks are not all the same length, so their entitlement cannot be checked in blocks: ${awardedDays(crew)} days awarded against ${use.totalDays}.`}
        </Typography>
      )}
      {entitlementReached && !use.over && (
        <Typography className="panel_warning">
          <WarningIcon fontSize="inherit" />
          {fullEntitlement}
        </Typography>
      )}
      {capReached && (
        <Typography className="panel_warning">
          <WarningIcon fontSize="inherit" />
          {`Holds the maximum of ${maxAwardsFirstRound} round 1 awards.`}
        </Typography>
      )}
      <AwardWarnings warnings={openWarnings(crew)} onDismiss={onDismissWarning} />

      {crew.bid?.note && (
        <>
          <Typography className="panel_label">
            Package note<span className="panel_label_hint">applies to the whole bid</span>
          </Typography>
          <Typography className="panel_note">{crew.bid.note}</Typography>
        </>
      )}

      <Typography className="panel_label">
        {crew.bid ? `Ranked bids (${crew.bid.preferences.length})` : "Ranked bids"}
      </Typography>
      {!crew.bid && (
        <Typography className="panel_empty">No bid submitted. Award them a block from the grid above.</Typography>
      )}
      {crew.bid && (
        <RankedBidList
          preferences={crew.bid.preferences}
          slotsById={slotsById}
          usageBySlot={usageBySlot}
          showSlotIdYear={showSlotIdYear}
          initialCount={INITIAL_BIDS_SHOWN}
          awardedSlotIds={new Set(awardsBySlot.keys())}
          daysLabels={shortBlockLabels}
          renderStatus={(preference, slot, usage) => {
            const award = awardsBySlot.get(preference.slotId);
            if (award && award.round !== round) {
              return (
                <>
                  <span className="awarded_label">{`Awarded in round ${award.round}`}</span>
                  {shortBlockControl(preference.slotId)}
                </>
              );
            }
            if (award) {
              return (
                <>
                  <span className="awarded_label">Awarded</span>
                  {shortBlockControl(preference.slotId)}
                  {undoButton(preference.slotId, "Undo")}
                </>
              );
            }
            return (
              <>
                <SlotAvailability usage={usage} />
                {slot && (
                  <Tooltip title={awardBlockedReason} placement="top">
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        disabled={
                          !writesAllowed || capReached || entitlementReached || pendingAdds.has(preference.slotId)
                        }
                        onClick={() => onAward(preference.slotId)}
                        data-testid={VACATION_BID_TEST_ID.awardButton}
                      >
                        {pendingAdds.has(preference.slotId) ? "Awarding…" : "Award"}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
            );
          }}
        />
      )}

      {otherAwards.length > 0 && (
        <>
          <Typography className="panel_label">
            Other awards<span className="panel_label_hint">not on these ranked bids</span>
          </Typography>
          {otherAwards.map((award) => {
            const slot = slotsById.get(award.slotId);
            return (
              <div key={award.slotId} className="assigned_row">
                <Typography variant="body2" className="slot_id">
                  {displaySlotId(award.slotId, showSlotIdYear)}
                </Typography>
                <Typography variant="body2">{slot ? awardDateRange(award, slot) : ""}</Typography>
                <Typography variant="caption" className="other_source">
                  {awardSourceLabel(award)}
                  {shortBlockLabels.has(award.slotId) && ` · ${shortBlockLabels.get(award.slotId)}`}
                </Typography>
                <div className="assigned_actions">
                  {shortBlockControl(award.slotId)}
                  {award.round === round ? (
                    undoButton(award.slotId, "Unassign")
                  ) : (
                    <span className="awarded_label">{`Round ${award.round}`}</span>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      <CrewLeftoverDays
        crew={crew}
        round={round}
        slotsById={slotsById}
        showSlotIdYear={showSlotIdYear}
        writesAllowed={writesAllowed}
        pending={leftoverPending}
        pendingRemoves={pendingRemoves}
        onPlace={onPlaceLeftover}
      />
    </Box>
  );
};

export default CrewAwardPanel;
